import { useEffect, useMemo, useState } from 'react';
import type { ViewKey } from './shared/Sidebar';
import { getJSON, postJSON, parseCsvHeaders } from '../utils/api';
import type {
  SourceTemplate,
  TenantResult,
  ValidateResult,
  ImportResult,
  ImportJob,
} from '../types';
import { statusBadgeClass, titleCase } from '../utils/format';

const STEPS = ['Organization', 'Source & data', 'Field mapping', 'Validate', 'Import', 'Done'];
const ON_TIME_OPTS = ['arrived_by_date', 'arrived_before_noon', 'arrived_same_day'];

interface OnboardingProps {
  onNavigate: (view: ViewKey) => void;
  onTenantActivated: (tenant: TenantResult) => void;
}

export function Onboarding({ onNavigate, onTenantActivated }: OnboardingProps) {
  const [step, setStep] = useState(1);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // Step 1 — organization
  const [name, setName] = useState('Acme Furniture');
  const [currency, setCurrency] = useState('USD');
  const [agedDays, setAgedDays] = useState(60);
  const [onTime, setOnTime] = useState(ON_TIME_OPTS[0]);
  const [tenant, setTenant] = useState<TenantResult | null>(null);

  // Step 2 — source + data
  const [sources, setSources] = useState<SourceTemplate[] | null>(null);
  const [sourceKey, setSourceKey] = useState<string>('');
  const [csv, setCsv] = useState('');

  // Step 3 — mapping
  const [mapping, setMapping] = useState<Record<string, string>>({});

  // Step 4/5 — results
  const [validateRes, setValidateRes] = useState<ValidateResult | null>(null);
  const [mode, setMode] = useState<'append' | 'replace'>('replace');
  const [importRes, setImportRes] = useState<ImportResult | null>(null);
  const [jobs, setJobs] = useState<ImportJob[]>([]);
  const [importAttemptKey, setImportAttemptKey] = useState(() => crypto.randomUUID());

  useEffect(() => {
    getJSON<{ sources: SourceTemplate[] }>('/api/onboarding/sources')
      .then((d) => {
        setSources(d.sources);
        if (d.sources[0]) setSourceKey(d.sources[0].source_key);
      })
      .catch((e) => setErr(e instanceof Error ? e.message : 'Failed to load sources'));
  }, []);

  const source = useMemo(
    () => sources?.find((s) => s.source_key === sourceKey) ?? null,
    [sources, sourceKey],
  );
  const columns = useMemo(() => parseCsvHeaders(csv), [csv]);
  const targetFields = source?.target_fields ?? [];
  const nameMapped = Object.values(mapping).includes('name');

  async function run(fn: () => Promise<void>) {
    setBusy(true);
    setErr(null);
    try {
      await fn();
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Something went wrong');
    } finally {
      setBusy(false);
    }
  }

  function loadSample() {
    if (!source) return;
    setCsv(source.sample_csv);
    setMapping({ ...source.suggested_mapping });
  }

  function seedMappingFromColumns() {
    if (!source) return;
    const next: Record<string, string> = {};
    for (const col of columns) next[col] = source.suggested_mapping[col] ?? '';
    setMapping(next);
  }

  // ── step transitions ────────────────────────────────────────
  const createOrg = () =>
    run(async () => {
      const t = await postJSON<TenantResult>('/api/onboarding/tenants', {
        name,
        config: { currency, aged_inventory_threshold_days: agedDays, on_time_definition: onTime },
      });
      setTenant(t);
      onTenantActivated(t);
      setStep(2);
    });

  const toMapping = () => {
    seedMappingFromColumns();
    setStep(3);
  };

  const saveMappingAndValidate = () =>
    run(async () => {
      const clean = Object.fromEntries(Object.entries(mapping).filter(([, v]) => v));
      if (tenant && source) {
        await postJSON('/api/onboarding/mappings', {
          tenant_id: tenant.tenant_id,
          source_key: source.source_key,
          entity: source.entity,
          mapping: clean,
        });
      }
      const v = await postJSON<ValidateResult>('/api/onboarding/validate', {
        entity: source?.entity ?? 'suppliers',
        mapping: clean,
        csv,
      });
      setValidateRes(v);
      setStep(4);
    });

  const runImport = () =>
    run(async () => {
      const clean = Object.fromEntries(Object.entries(mapping).filter(([, v]) => v));
      const r = await postJSON<ImportResult>('/api/onboarding/import', {
        tenant_id: tenant?.tenant_id,
        source_key: source?.source_key,
        entity: source?.entity ?? 'suppliers',
        mapping: clean,
        csv,
        mode,
        idempotency_key: importAttemptKey,
      });
      setImportRes(r);
      const j = await getJSON<{ items: ImportJob[] }>(
        `/api/onboarding/jobs?tenant_id=${tenant?.tenant_id ?? ''}`,
      );
      setJobs(j.items);
      setStep(6);
    });

  function restart() {
    setStep(1);
    setTenant(null);
    setCsv('');
    setMapping({});
    setValidateRes(null);
    setImportRes(null);
    setJobs([]);
    setImportAttemptKey(crypto.randomUUID());
    setErr(null);
  }

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      {/* Stepper */}
      <ol className="flex flex-wrap items-center gap-2 text-sm">
        {STEPS.map((label, i) => {
          const n = i + 1;
          const state = n < step ? 'done' : n === step ? 'active' : 'todo';
          return (
            <li key={label} className="flex items-center gap-2">
              <span
                className={`flex h-6 w-6 items-center justify-center rounded-full text-xs font-semibold ${
                  state === 'active'
                    ? 'bg-brand-500 text-white'
                    : state === 'done'
                      ? 'bg-emerald-500 text-white'
                      : 'bg-slate-200 text-slate-500'
                }`}
              >
                {state === 'done' ? '✓' : n}
              </span>
              <span className={state === 'active' ? 'font-medium text-slate-900' : 'text-slate-500'}>
                {label}
              </span>
              {n < STEPS.length && <span className="text-slate-300">›</span>}
            </li>
          );
        })}
      </ol>

      {err && (
        <div className="rounded-lg border border-rose-200 bg-rose-50 px-4 py-2 text-sm text-rose-700">
          {err}
        </div>
      )}

      {/* ── Step 1: Organization ── */}
      {step === 1 && (
        <section className="card space-y-4">
          <div>
            <h2 className="text-lg font-semibold text-slate-900">Create your organization</h2>
            <p className="text-sm text-slate-500">
              Define the tenant and its business rules. These drive how metrics are interpreted.
            </p>
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field label="Organization name">
              <input className="input" value={name} onChange={(e) => setName(e.target.value)} />
            </Field>
            <Field label="Currency">
              <input className="input" value={currency} onChange={(e) => setCurrency(e.target.value)} />
            </Field>
            <Field label="Aged-inventory threshold (days)">
              <input
                type="number"
                className="input"
                value={agedDays}
                onChange={(e) => setAgedDays(Number(e.target.value))}
              />
            </Field>
            <Field label="On-time definition">
              <select className="input" value={onTime} onChange={(e) => setOnTime(e.target.value)}>
                {ON_TIME_OPTS.map((o) => (
                  <option key={o} value={o}>
                    {titleCase(o)}
                  </option>
                ))}
              </select>
            </Field>
          </div>
          <Footer>
            <button className="btn-primary" disabled={!name || busy} onClick={createOrg}>
              {busy ? 'Saving…' : 'Create org & continue'}
            </button>
          </Footer>
        </section>
      )}

      {/* ── Step 2: Source & data ── */}
      {step === 2 && (
        <section className="card space-y-4">
          <div>
            <h2 className="text-lg font-semibold text-slate-900">Choose a source & load data</h2>
            <p className="text-sm text-slate-500">
              Pick a source system template, then paste a CSV export (or load the sample).
            </p>
          </div>
          <Field label="Source template">
            <select className="input" value={sourceKey} onChange={(e) => setSourceKey(e.target.value)}>
              {(sources ?? []).map((s) => (
                <option key={s.source_key} value={s.source_key}>
                  {s.label} → {s.entity}
                </option>
              ))}
            </select>
          </Field>
          <div className="flex items-center justify-between">
            <label className="text-sm font-medium text-slate-600">CSV data</label>
            <button className="btn-ghost" onClick={loadSample} disabled={!source}>
              Load sample CSV
            </button>
          </div>
          <textarea
            className="input h-44 font-mono text-xs"
            placeholder="Paste CSV with a header row…"
            value={csv}
            onChange={(e) => setCsv(e.target.value)}
          />
          {columns.length > 0 && (
            <p className="text-xs text-slate-400">
              Detected {columns.length} columns: {columns.join(', ')}
            </p>
          )}
          <Footer>
            <button className="btn-ghost" onClick={() => setStep(1)}>
              Back
            </button>
            <button className="btn-primary" disabled={columns.length === 0} onClick={toMapping}>
              Continue
            </button>
          </Footer>
        </section>
      )}

      {/* ── Step 3: Mapping ── */}
      {step === 3 && (
        <section className="card space-y-4">
          <div>
            <h2 className="text-lg font-semibold text-slate-900">Map fields</h2>
            <p className="text-sm text-slate-500">
              Map each source column to a canonical {source?.entity} field. Unmapped columns are ignored.
            </p>
          </div>
          <div className="space-y-2">
            {columns.map((col) => (
              <div key={col} className="flex items-center gap-3">
                <span className="w-1/2 truncate rounded-md bg-slate-100 px-3 py-1.5 text-sm font-medium text-slate-700">
                  {col}
                </span>
                <span className="text-slate-300">→</span>
                <select
                  className="input w-1/2"
                  value={mapping[col] ?? ''}
                  onChange={(e) => setMapping((m) => ({ ...m, [col]: e.target.value }))}
                >
                  <option value="">— skip —</option>
                  {targetFields.map((f) => (
                    <option key={f.field} value={f.field}>
                      {f.field}
                      {f.required ? ' *' : ''}
                    </option>
                  ))}
                </select>
              </div>
            ))}
          </div>
          <p className="text-xs text-slate-400">
            Required fields marked <span className="font-semibold">*</span>.{' '}
            {targetFields.find((f) => f.required)?.note}
          </p>
          <Footer>
            <button className="btn-ghost" onClick={() => setStep(2)}>
              Back
            </button>
            <button
              className="btn-primary"
              disabled={!nameMapped || busy}
              title={nameMapped ? '' : 'Map a column to "name" first'}
              onClick={saveMappingAndValidate}
            >
              {busy ? 'Validating…' : 'Save mapping & validate'}
            </button>
          </Footer>
        </section>
      )}

      {/* ── Step 4: Validate ── */}
      {step === 4 && validateRes && (
        <section className="card space-y-4">
          <div>
            <h2 className="text-lg font-semibold text-slate-900">Validation results</h2>
            <p className="text-sm text-slate-500">A dry run — nothing has been written yet.</p>
          </div>
          <div className="grid grid-cols-3 gap-3 text-center">
            <Stat label="Rows" value={validateRes.total} />
            <Stat label="Importable" value={validateRes.rows_importable} tone="good" />
            <Stat label="Skipped" value={validateRes.rows_skipped} tone={validateRes.rows_skipped ? 'bad' : 'neutral'} />
          </div>
          {validateRes.preview.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs uppercase tracking-wide text-slate-400">
                    {Object.keys(validateRes.preview[0]).map((k) => (
                      <th key={k} className="pb-2 pr-3">
                        {k}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {validateRes.preview.map((row, i) => (
                    <tr key={i}>
                      {Object.keys(validateRes.preview[0]).map((k) => (
                        <td key={k} className="py-1.5 pr-3 text-slate-600">
                          {String(row[k] ?? '—')}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {validateRes.errors.length > 0 && (
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
              <p className="mb-1 font-medium">{validateRes.errors.length} row(s) will be skipped:</p>
              <ul className="list-inside list-disc">
                {validateRes.errors.slice(0, 8).map((e, i) => (
                  <li key={i}>
                    Row {e.row}: {e.message}
                  </li>
                ))}
              </ul>
            </div>
          )}
          <Footer>
            <button className="btn-ghost" onClick={() => setStep(3)}>
              Back
            </button>
            <button
              className="btn-primary"
              disabled={validateRes.rows_importable === 0}
              onClick={() => {
                setImportAttemptKey(crypto.randomUUID());
                setStep(5);
              }}
            >
              Continue to import
            </button>
          </Footer>
        </section>
      )}

      {/* ── Step 5: Import ── */}
      {step === 5 && validateRes && (
        <section className="card space-y-4">
          <div>
            <h2 className="text-lg font-semibold text-slate-900">Run import</h2>
            <p className="text-sm text-slate-500">
              Write {validateRes.rows_importable} {source?.entity} into the operational layer.
            </p>
          </div>
          <div className="space-y-2">
            <label className="flex items-start gap-3 rounded-lg border border-slate-200 p-3">
              <input type="radio" checked={mode === 'replace'} onChange={() => setMode('replace')} className="mt-1" />
              <span>
                <span className="font-medium text-slate-800">Replace sample data</span>
                <span className="block text-xs text-slate-500">
                  Clear existing {source?.entity} first, then load yours. Best for a clean demo.
                </span>
              </span>
            </label>
            <label className="flex items-start gap-3 rounded-lg border border-slate-200 p-3">
              <input type="radio" checked={mode === 'append'} onChange={() => setMode('append')} className="mt-1" />
              <span>
                <span className="font-medium text-slate-800">Append</span>
                <span className="block text-xs text-slate-500">Add to whatever is already there.</span>
              </span>
            </label>
          </div>
          <Footer>
            <button className="btn-ghost" onClick={() => setStep(4)}>
              Back
            </button>
            <button className="btn-primary" disabled={busy} onClick={runImport}>
              {busy ? 'Importing…' : `Import ${validateRes.rows_importable} rows`}
            </button>
          </Footer>
        </section>
      )}

      {/* ── Step 6: Done ── */}
      {step === 6 && importRes && (
        <section className="card space-y-4">
          <div className="flex items-center gap-3">
            <span className="flex h-10 w-10 items-center justify-center rounded-full bg-emerald-100 text-xl">
              🎉
            </span>
            <div>
              <h2 className="text-lg font-semibold text-slate-900">
                Onboarded {tenant?.name ?? 'organization'}
              </h2>
              <p className="text-sm text-slate-500">
                Imported {importRes.rows_imported} {importRes.entity}
                {importRes.rows_skipped > 0 && `, skipped ${importRes.rows_skipped}`} ·{' '}
                <span className={`badge ${statusBadgeClass(importRes.status)}`}>{importRes.status}</span>
              </p>
            </div>
          </div>

          {jobs.length > 0 && (
            <div>
              <h3 className="mb-2 text-sm font-semibold text-slate-700">Import history</h3>
              <ul className="divide-y divide-slate-100 text-sm">
                {jobs.map((j) => (
                  <li key={j.id} className="flex items-center justify-between py-2">
                    <span className="text-slate-600">
                      #{j.id} · {j.entity} · {j.mode}
                    </span>
                    <span className="text-slate-500">
                      {j.rows_imported} imported
                      <span className={`badge ml-2 ${statusBadgeClass(j.status)}`}>{j.status}</span>
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <p className="rounded-lg bg-slate-50 p-3 text-xs text-slate-500">
            Supplier master data is now tenant-scoped and traceable to this source/import.
            Performance remains explicitly “not measured” until a performance feed is connected.
          </p>

          <Footer>
            <button className="btn-ghost" onClick={restart}>
              Onboard another
            </button>
            <button className="btn-primary" onClick={() => onNavigate('suppliers')}>
              View Supplier Health →
            </button>
          </Footer>
        </section>
      )}
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="mb-1 block text-sm font-medium text-slate-600">{label}</span>
      {children}
    </label>
  );
}

function Footer({ children }: { children: React.ReactNode }) {
  return <div className="flex justify-between gap-3 pt-2">{children}</div>;
}

function Stat({ label, value, tone = 'neutral' }: { label: string; value: number; tone?: 'neutral' | 'good' | 'bad' }) {
  const color = tone === 'good' ? 'text-emerald-600' : tone === 'bad' ? 'text-rose-600' : 'text-slate-900';
  return (
    <div className="rounded-lg border border-slate-200 p-3">
      <p className={`text-2xl font-semibold ${color}`}>{value}</p>
      <p className="text-xs text-slate-500">{label}</p>
    </div>
  );
}
